import type { TFunction } from 'i18next';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { CryptoOnchainPayment, CryptoOnchainPaymentStatus } from '@/lib/api/crypto-onchain';
import { formatDate } from '@/lib/format';
import { safeHttpUrl } from '@/app/manage/crypto-payments/components/format';

const EMPTY_VALUE = '—';

type Props = {
  payments: CryptoOnchainPayment[];
  isLoading: boolean;
  /** "Check status" on a row: the server re-reads the crypto-api session and reconciles the payment. */
  onCheckPayment: (cryptoOnchainPaymentId: string) => void;
  /** The row whose check is in flight (its button waits); null when none is. */
  checkingPaymentId: string | null;
};

function getPaymentStatusBadge(
  status: CryptoOnchainPaymentStatus,
  t: TFunction<'integrations'>,
): { variant: BadgeVariant; label: string } {
  switch (status) {
    case 'paid':
      return { variant: 'green', label: t('cryptoOnchain.payments.status.paid') };
    case 'paid_late':
      return { variant: 'green', label: t('cryptoOnchain.payments.status.paidLate') };
    case 'detected':
      return { variant: 'blue', label: t('cryptoOnchain.payments.status.detected') };
    case 'pending':
      return { variant: 'yellow', label: t('cryptoOnchain.payments.status.pending') };
    case 'underpaid':
      return { variant: 'orange', label: t('cryptoOnchain.payments.status.underpaid') };
    case 'overpaid':
      return { variant: 'orange', label: t('cryptoOnchain.payments.status.overpaid') };
    case 'expired':
      return { variant: 'gray', label: t('cryptoOnchain.payments.status.expired') };
    case 'failed':
      return { variant: 'red', label: t('cryptoOnchain.payments.status.failed') };
    default:
      return { variant: 'secondary', label: status };
  }
}

function shortenHash(hash: string): string {
  return hash.length <= 16 ? hash : `${hash.slice(0, 8)}…${hash.slice(-6)}`;
}

function formatCryptoAmount(payment: CryptoOnchainPayment): string {
  if (!payment.amountFormatted || !payment.asset) return EMPTY_VALUE;
  return `${payment.amountFormatted} ${payment.asset}`;
}

function formatChargedAmount(payment: CryptoOnchainPayment): string {
  if (!payment.fiatAmount || !payment.fiatCurrency) return EMPTY_VALUE;
  const charged = `${payment.fiatAmount} ${payment.fiatCurrency}`;
  const hasOriginal =
    payment.originalAmount && payment.originalCurrency && payment.originalCurrency !== payment.fiatCurrency;
  return hasOriginal ? `${charged} (${payment.originalAmount} ${payment.originalCurrency})` : charged;
}

/**
 * The latest on-chain payments of the store, newest first, with the crypto-api session status. A
 * duplicate (paid after the order was settled by another payment) is flagged for a manual refund;
 * "Check status" reconciles one payment against crypto-api when a webhook went missing.
 */
export function CryptoOnchainPaymentsTable({
  payments,
  isLoading,
  onCheckPayment,
  checkingPaymentId,
}: Props) {
  const { t } = useTranslation('integrations');

  const rows = payments.map((payment) => ({
    id: payment.id,
    date: formatDate(payment.createdAt),
    orderId: payment.orderId,
    amount: formatCryptoAmount(payment),
    charged: formatChargedAmount(payment),
    badge: getPaymentStatusBadge(payment.status, t),
    isDuplicate: payment.duplicate,
    isTest: payment.test,
    isRejected: payment.rejected,
    txHash: payment.txHash ? shortenHash(payment.txHash) : EMPTY_VALUE,
    txHashFull: payment.txHash ?? undefined,
    explorerUrl: safeHttpUrl(payment.explorerTxUrl),
    isChecking: checkingPaymentId === payment.id,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('cryptoOnchain.payments.title')}</CardTitle>
        <CardDescription>{t('cryptoOnchain.payments.description')}</CardDescription>
        <p className="text-muted-foreground text-xs">{t('cryptoOnchain.payments.refundsUnavailable')}</p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('cryptoOnchain.payments.empty')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('cryptoOnchain.payments.columns.date')}</TableHead>
                <TableHead>{t('cryptoOnchain.payments.columns.order')}</TableHead>
                <TableHead>{t('cryptoOnchain.payments.columns.amount')}</TableHead>
                <TableHead>{t('cryptoOnchain.payments.columns.charged')}</TableHead>
                <TableHead>{t('cryptoOnchain.payments.columns.status')}</TableHead>
                <TableHead>{t('cryptoOnchain.payments.columns.transaction')}</TableHead>
                <TableHead className="text-right">{t('cryptoOnchain.payments.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap">{row.date}</TableCell>
                  <TableCell>
                    <code className="text-xs">{row.orderId}</code>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{row.amount}</TableCell>
                  <TableCell className="whitespace-nowrap">{row.charged}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant={row.badge.variant}>{row.badge.label}</Badge>
                      {row.isDuplicate && (
                        <Badge variant="red">{t('cryptoOnchain.payments.duplicateBadge')}</Badge>
                      )}
                      {row.isTest && <Badge variant="blue">{t('cryptoOnchain.payments.testBadge')}</Badge>}
                      {row.isRejected && (
                        <Badge variant="red">{t('cryptoOnchain.payments.orderNotPaidBadge')}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {row.explorerUrl ? (
                      <a
                        href={row.explorerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={row.txHashFull}
                        className="inline-flex items-center gap-1 font-mono text-xs underline-offset-2 hover:underline"
                      >
                        {row.txHash}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <code className="text-xs" title={row.txHashFull}>
                        {row.txHash}
                      </code>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={row.isChecking}
                      onClick={() => onCheckPayment(row.id)}
                    >
                      {row.isChecking && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                      {row.isChecking
                        ? t('cryptoOnchain.payments.checking')
                        : t('cryptoOnchain.payments.checkStatus')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
